import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useFormContext } from '../Context/Context';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useReadOnly } from '../Context/ReadOnlyContext';
import { FinalValuesContext } from '../Context/FinalValuesContext';
import ConvertTitle from './ConvertTitle';
import CustomToast from './CustomToast/CustomToast';
import FileUploadUI from './FileUploadUI/FileUploadUI';
import { handleConditionLogic, isZipOrPdf, syncDrawerState } from '../helpers/FileUpload/fileUploadHelpers';

const FileUpload = ({ id, header }) => {
    const { drawerData } = useFormContext();
    const location = useLocation();
    const currentPage = location.pathname;
    const existingComponentIndex = drawerData.findIndex(component => component.id === id);
    const switchHeader = header || drawerData[existingComponentIndex]?.state.fileValue || 'آپلود فایل';
    const switchSubName = drawerData[existingComponentIndex]?.state.subFileValue || '';
    const isReadOnly = currentPage.startsWith('/publish') || currentPage.startsWith("/card-form/publish") || currentPage.startsWith("/user/fill-form/");

    const { readOnly } = useReadOnly();
    const { finalSelectValues } = useContext(FinalValuesContext);

    const [selectedFile, setSelectedFile] = useState(null);
    const [fileValue, setFileValue] = useState(switchHeader);
    const [fileInputValue, setFileInputValue] = useState(drawerData[existingComponentIndex]?.state.mainFileInputValue);
    const [subFileValue, setSubFileValue] = useState(switchSubName);
    const [isSubHeaderVisible, setSubHeaderVisible] = useState(false);
    const [isHeadingFocused, setHeadingFocused] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState(drawerData[existingComponentIndex]?.state.mainSelectedFileName);
    const [upload, setUpload] = useState(false);
    const [condition, setCondition] = useState(false);

    const containerRef = useRef(null);

    const titleIndex = finalSelectValues.findIndex(item => item.name === "FieldCondition");
    const title = ConvertTitle({ title: finalSelectValues[titleIndex]?.value });
    const dataIndex = drawerData.findIndex(item => item.title === title);
    const dataCondition = drawerData[dataIndex]?.state.readOnly;

    const handleFileSelectChange = (e) => {
        const file = e.target.files[0];
        if (file && isZipOrPdf(file)) {
            setSelectedFileName(file.name);
            setFileInputValue(e.target.value);
            setSelectedFile(file);
            setUpload(true);
            toast(<CustomToast message="آپلود فایل با موفقیت انجام شد" state={'success'} />);
        } else {
            toast(<CustomToast message="لطفاً یک فایل معتبر (زیپ، pdf یا ویدیو) انتخاب کنید." state={'error'} />);
        }
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && isZipOrPdf(file)) {
            setSelectedFileName(file.name);
        } else {
            toast(<CustomToast message="لطفا یک فایل زیپ یا پی دی اف معتبر ارسال کنید." state={'error'} />);
        }
    };

    const handleFileChange = (e) => setFileValue(e.target.value);
    const handleSubFileChange = (e) => setSubFileValue(e.target.value);
    const handleHeadingFocus = (e) => {
        setHeadingFocused(true);
        e.target.select();
    };

    const handleFileUpload = () => { };

    const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
            setSubHeaderVisible(false);
            setHeadingFocused(false);
        } else {
            setSubHeaderVisible(true);
            setHeadingFocused(true);
        }
    };

    useEffect(() => {
        syncDrawerState({
            drawerData,
            existingComponentIndex,
            selectedFile,
            fileValue,
            fileInputValue,
            subFileValue,
            selectedFileName,
            readOnly
        });
    }, [id, selectedFile, fileInputValue, selectedFileName, fileValue, subFileValue]);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        handleConditionLogic(currentPage, readOnly, dataCondition, title, setCondition);
    }, [finalSelectValues, readOnly]);

    const theme = useTheme();
    const isUpXxl = useMediaQuery((theme) => theme.breakpoints.up('xxs'));
    const SubHeadingInputFontSize = isUpXxl ? '14px' : '12px';
    const HeadingColor = theme.palette.blue[700];

    return (
        <Grid
            mb={currentPage.startsWith('/build') || currentPage.startsWith('/publish') ? '28px' : '50px'}
            p={drawerData.length > 0 ? (currentPage.startsWith('/build') || currentPage.startsWith('/publish') || currentPage.startsWith('/user/fill-form') ? '12px 10px' : { xs: '12px', md: '24px' }) : '0px'}
            mx={currentPage === '/build' || currentPage === '/publish' ? '4px' : '0px'}
            borderRadius={'3px'}
            width={'100%'}
            bgcolor={'white'}
            ref={containerRef}
            border={`2px solid ${isHeadingFocused && drawerData.length > 0 && drawerData[existingComponentIndex] && !isReadOnly ? '#0293e2' : 'transparent'}`}
            onClick={handleClickOutside}
        >
            <Grid className={'HeadingInput'} pb={'14px'}>
                <input
                    className="customHead-input"
                    placeholder={'File Upload'}
                    style={{
                        border: 'none',
                        outline: 'none',
                        userSelect: 'text',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        width: '100%',
                        fontSize: '16px',
                        fontWeight: 700,
                        lineHeight: 1.45,
                        color: HeadingColor
                    }}
                    readOnly={isReadOnly || condition}
                    value={fileValue}
                    onChange={handleFileChange}
                    onClick={handleClickOutside}
                    onFocus={isReadOnly ? null : handleHeadingFocus}
                />
            </Grid>

            <FileUploadUI
                condition={condition}
                upload={upload}
                selectedFileName={selectedFileName}
                handleFileUpload={handleFileUpload}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                handleFileSelectChange={handleFileSelectChange}
            />

            <Grid>
                {isSubHeaderVisible && (
                    <Grid className={'subHeader'} pb={'14px'} display={isReadOnly && subFileValue === '' ? 'none' : 'block'}>
                        <input
                            placeholder={'یک عنوان فرعی وارد کنید'}
                            style={{
                                border: 'none',
                                outline: 'none',
                                userSelect: 'text',
                                wordWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                width: '100%',
                                fontSize: SubHeadingInputFontSize,
                                fontWeight: 500,
                                lineHeight: 1.6,
                                color: '#57647e'
                            }}
                            readOnly={isReadOnly || condition}
                            value={subFileValue}
                            onChange={handleSubFileChange}
                        />
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default FileUpload;